import { AxiosInstance } from 'axios'
import { InjectionKey } from '@vue/composition-api'
import * as Z from 'zod'
//import { ApiResponse } from '@/rest/responses'
import { axiosApiResponse } from '../../../rest/api.axios'
import {
  validateSchema,
  validateUnknown,
  validateVoid
} from '../../../rest/api.zod'

export type TranscriptId = number
export type TranscriptVersion = number
export type Speaker = string
export type Milliseconds = number

export interface TranscriptItem {
  readonly start?: Milliseconds
  readonly end?: Milliseconds
  readonly speaker?: Speaker
  readonly text?: string
}

export interface Identifiable {
  readonly id?: number
}

export type TranscriptWords = Array<TranscriptItem & Identifiable>

export interface TranscriptPayload {
  readonly version: number
  readonly words: TranscriptWords
}

export interface Textual {
  text: string
}

export interface Spoken {
  speaker: string
}

export type SpokenPart = Textual & Spoken
export type SpokenParts = Array<SpokenPart>

export interface TranscriptPayloadV2 {
  readonly version: number
  readonly parts: SpokenParts
}

export const transcriptItemSchema = Z.object({
  id: Z.number()
    .int()
    .optional(),
  speaker: Z.string(),
  start: Z.number().int(),
  end: Z.number().int(),
  text: Z.string()
})

export const transcriptWordSchema = Z.object({
  id: Z.number().int(),
  speaker: Z.string(),
  start: Z.number().int(),
  end: Z.number().int(),
  text: Z.string()
})
export type TranscriptWordSchema = Z.TypeOf<typeof transcriptWordSchema>

export type TopicTagId = number

export interface TopicTag {
  id: TopicTagId
  readonly transcriptId: TranscriptId
  readonly transcriptVersion: number
  readonly createdAt: string | null
  readonly editedAt?: string | null
  readonly createdBy?: string
  readonly editedBy?: string
  readonly type: string
  readonly category: string
  readonly subCategory: string
  readonly startWordIndex: number
  readonly endWordIndex: number
  readonly startWordIndexPart: number
  readonly endWordIndexPart: number
  readonly partIndex: number
  readonly speaker: string
  readonly note?: string | null
  readonly flagged: boolean | null
  readonly deleted: boolean | null
  readonly startTime?: string | null
  readonly endTime?: string | null
}

/** Newly created topic tags do not have ids (yet) */
export type NewTopicTag = Omit<TopicTag, 'id'>

export const newTopicTagSchemaTemplate = {
  transcriptId: Z.number().int(),
  transcriptVersion: Z.number().int(),
  createdAt: Z.string().nullable(),
  editedAt: Z.string().nullable(),
  createdBy: Z.string().optional(),
  editedBy: Z.string()
    .nullable()
    .optional(),
  type: Z.string(),
  category: Z.string(),
  subCategory: Z.string(),
  startWordIndex: Z.number()
    .int()
    .nullable(),
  endWordIndex: Z.number()
    .int()
    .nullable(),
  startWordIndexPart: Z.number()
    .int()
    .nullable(),
  endWordIndexPart: Z.number()
    .int()
    .nullable(),
  partIndex: Z.number()
    .int()
    .nullable(),
  speaker: Z.string(),
  note: Z.string()
    .nullable()
    .optional(),
  flagged: Z.boolean().nullable(),
  deleted: Z.boolean().nullable(),
  startTime: Z.number()
    .nullable()
    .optional(),
  endTime: Z.number()
    .nullable()
    .optional()
}

/** A new topic tag schema omits the topic id as it is unknown. */
export const newTopicTagSchema = Z.object(newTopicTagSchemaTemplate)

/** Existing topic tag schemas include the topic id as it is known. */
export const topicTagSchema = Z.object({
  id: Z.number().int(),
  ...newTopicTagSchemaTemplate
})

export const transcriptSchema = Z.object({
  id: Z.number().int(),
  version: Z.number().int(),
  parts: Z.array(transcriptItemSchema),
  sentences: Z.array(transcriptItemSchema),
  words: Z.array(transcriptWordSchema),
  topicTags: Z.array(topicTagSchema),
  versions: Z.array(Z.number().int()).optional()
})

export type Transcript = Partial<Z.infer<typeof transcriptSchema>>

export type SaveableTranscript = Omit<
  Omit<Omit<Omit<Transcript, 'parts'>, 'sentences'>, 'topicTags'>,
  'versions'
>

export type TopicId = string // why did this change to string???

export interface Topic {
  readonly id: number
  readonly client_id: string
  readonly project_id: string
  readonly category: string | null
  readonly sub_category: string | null
  readonly color: number | null
  readonly type: string
  readonly definition: string | null
  readonly include_in_score: boolean | null
  readonly count_required: number | null
  readonly roadmap: boolean | null
  readonly magnifier: boolean | null
  readonly icon: string | null
  readonly main_toggle: boolean | null
  readonly common_name: string | null
}

export const topicSchema = Z.object({
  id: Z.number().int(),
  client_id: Z.string().nullable(),
  project_id: Z.string().nullable(),
  category: Z.string().nullable(),
  sub_category: Z.string().nullable(),
  type: Z.string().nullable(),
  colour: Z.number()
    .int()
    .nullable(),
  definition: Z.string().nullable(),
  include_in_score: Z.boolean().nullable(),
  count_required: Z.number()
    .int()
    .nullable(),
  main_toggle: Z.boolean().nullable(),
  common_name: Z.string().nullable(),
  roadmap: Z.boolean().nullable(),
  magnifier: Z.boolean().nullable(),
  icon: Z.string().nullable()
})

export interface TranscriptionData {
  readonly transcript: Transcript
  readonly topics: Array<Topic>
}

export const transcriptionDataSchema = Z.object({
  transcript: transcriptSchema,
  topics: Z.array(topicSchema)
})

export const validateTranscript = validateSchema(transcriptSchema)
export const validateTag = validateSchema(topicTagSchema)
export const validateTags = validateSchema(Z.array(topicTagSchema))
export const handleTranscriptResponse = axiosApiResponse(validateTranscript)
export const handleTagResponse = axiosApiResponse(validateTag)
export const handleTagsResponse = axiosApiResponse(validateTags)
export const handleVoidResponse = axiosApiResponse(validateVoid)
export const handleUnknownResponse = axiosApiResponse(validateUnknown)

export const validateTranscriptionData = validateSchema(transcriptionDataSchema)
export const handleTranscriptionDataResponse = axiosApiResponse(
  validateTranscriptionData
)

export const createTranscriptionV2Api = (
  client: AxiosInstance,
  uri: string
) => {
  const path = (suffix: string) => uri.concat(suffix)

  // TODO: Return types...
  const loadTranscript = (id: TranscriptId) => {
    return handleTranscriptionDataResponse(
      client.get(path(`/transcripts/${id}`))
    )
  }

  const loadTranscriptScores = (id: TranscriptId) =>
    handleUnknownResponse(client.get(path(`/transcripts/${id}/scores`)))

  const saveTranscript = (
    id: TranscriptId,
    transcript: TranscriptPayload,
    publish: boolean
  ) =>
    handleVoidResponse(
      client.put(path(`/transcripts/${id}`), { ...transcript, publish })
    )

  const loadTags = (id: TranscriptId) =>
    handleTagsResponse(client.get(path(`/transcripts/${id}/tags`)))

  const saveTranscriptV2 = (
    id: TranscriptId,
    transcript: TranscriptPayloadV2,
    publish: boolean
  ) =>
    handleTranscriptionDataResponse(
      client.put(path(`/transcripts/v2/${id}`), { ...transcript, publish })
    )

  const saveTranscriptV3 = (
    id: TranscriptId,
    transcript: TranscriptPayloadV2,
    language: string
  ) =>
    handleTranscriptResponse(
      client.put(path(`/transcripts/v3/${id}/save`), {
        ...transcript,
        language
      })
    )

  const transcriptCalculations = (id: TranscriptId, version: number) =>
    handleTranscriptResponse(
      client.post(path(`/transcripts/v3/${id}/calculate`), { version })
    )

  const clearAutomatedData = (
    id: TranscriptId,
    version: number,
    publish: boolean
  ) =>
    handleTranscriptResponse(
      client.post(path(`/transcripts/v3/${id}/clear`), { version, publish })
    )

  const publishTranscript = (
    id: TranscriptId,
    version: number,
    publish: boolean
  ) =>
    handleTranscriptResponse(
      client.post(path(`/transcripts/v3/${id}/publish`), { version, publish })
    )
  const startTagging = (id: TranscriptId, transcript: TranscriptPayloadV2) =>
    handleTranscriptResponse(
      client.post(path(`/transcripts/v2/${id}/tags`), { ...transcript })
    )
  const swapSpeakers = (id: TranscriptId) =>
    handleTranscriptResponse(
      client.post(path(`/transcripts/${id}/swap-speakers`))
    )

  const addTag = (id: TranscriptId, topicTag: NewTopicTag) =>
    handleTagResponse(client.post(path(`/transcripts/${id}/tags`), topicTag))

  const upvoteTag = (id: TranscriptId, tagId: TopicTagId) =>
    handleVoidResponse(
      client.post(path(`/transcripts/${id}/tags/${tagId}/upvote`))
    )

  const updateTag = (
    id: TranscriptId,
    tagId: TopicTagId,
    note: string,
    sub_category: string,
    flagged: boolean,
    deleted?: boolean
  ) =>
    handleVoidResponse(
      client.post(path(`/transcripts/${id}/tags/${tagId}/update`), {
        note,
        sub_category,
        flagged,
        deleted
      })
    )

  const downvoteTag = (id: TranscriptId, tagId: TopicTagId) =>
    handleVoidResponse(
      client.post(path(`/transcripts/${id}/tags/${tagId}/downvote`))
    )

  const commentOnTag = (id: TranscriptId, tagId: TopicTagId, comment: string) =>
    handleVoidResponse(
      client.post(path(`/transcripts/${id}/tags/${tagId}/comments`), {
        comment
      })
    )

  const deleteTag = (id: TranscriptId, tagId: TopicTagId) =>
    handleVoidResponse(client.delete(path(`/transcripts/${id}/tags/${tagId}`)))

  return {
    loadTranscript,
    loadTranscriptScores,
    saveTranscript,
    saveTranscriptV2,
    saveTranscriptV3,
    transcriptCalculations,
    clearAutomatedData,
    publishTranscript,
    startTagging,
    swapSpeakers,
    updateTag,
    loadTags,
    addTag,
    upvoteTag,
    downvoteTag,
    commentOnTag,
    deleteTag
  }
}

export type TranscriptionV2Api = ReturnType<typeof createTranscriptionV2Api>
export const TranscriptionV2ApiKey: InjectionKey<TranscriptionV2Api> = Symbol()
